import { useState, useEffect, useCallback } from 'react';
import { endpointPortfolioHasBenchmarkGET } from '~/api/def/endpoints/portfolioAnalysisEndpoint';

interface UseHasBenchmarkResult {
  hasBenchmark: boolean;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to check if a portfolio has a benchmark configured.
 * Uses the dedicated REST endpoint for efficient benchmark checking.
 *
 * @param portfolioId - The ID of the portfolio to check
 * @returns Object containing hasBenchmark flag, loading state, error, and refetch function
 */
export const useHasBenchmark = (
  portfolioId: string | null | undefined,
): UseHasBenchmarkResult => {
  const [hasBenchmark, setHasBenchmark] = useState<boolean>(false);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  // Track which portfolioId we've resolved to avoid race conditions
  const [resolvedPortfolioId, setResolvedPortfolioId] = useState<
    string | null | undefined
  >(undefined);

  const fetchHasBenchmark = useCallback(async () => {
    if (!portfolioId) {
      setHasBenchmark(false);
      setIsFetching(false);
      setError(null);
      setResolvedPortfolioId(null);
      return;
    }

    setIsFetching(true);
    setError(null);

    try {
      const response = await endpointPortfolioHasBenchmarkGET(portfolioId);
      setHasBenchmark(response?.has_benchmark ?? false);
    } catch (err) {
      console.error('Failed to check portfolio benchmark:', err);
      setError(err as Error);
      setHasBenchmark(false);
    } finally {
      setIsFetching(false);
      setResolvedPortfolioId(portfolioId);
    }
  }, [portfolioId]);

  useEffect(() => {
    fetchHasBenchmark();
  }, [fetchHasBenchmark]);

  // isLoading is true if:
  // 1. We have a portfolioId but haven't resolved for this specific portfolioId yet
  // 2. Or we're currently fetching
  // This prevents consumers from proceeding before we know the benchmark state
  const isLoading =
    isFetching || (!!portfolioId && resolvedPortfolioId !== portfolioId);

  return {
    hasBenchmark,
    isLoading,
    error,
    refetch: fetchHasBenchmark,
  };
};
